import { useState } from "react";

import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import type { UserSettings } from "@app/common/types";
import * as UserSettingsUtils from "@app/common/utils/userSettingsUtils";
import type { EnterpriseRole } from "@app/common/workspaces/types";

import type {
  CreateWorkspaceSettings,
  UpdateCreateWorkspaceSetting,
  UpdateCreateWorkspaceSettings,
} from "../WorkspaceWizard/types";

const DEFAULT_SHARE_TEAM_DATA = {
  view: "admin" as const,
  edit: "admin" as const,
};

const DEFAULT_CAN_EDIT = true;

const buildDefaultSettings = (
  userSettings: UserSettings
): CreateWorkspaceSettings => {
  const emailRecapSettings =
    UserSettingsUtils.getEnabledUserDistributionSettingForCategoryAndProvider(
      userSettings,
      { category: "meeting_report", provider: "email" }
    );

  return {
    transcriptionOptIn: userSettings.transcriptionOptIn,
    storeRecordingOptIn: userSettings.storeRecordingOptIn,
    calendarJoinInternalOnly: userSettings.calendarOnlyJoinInternal,
    calendarPermissionLevel: userSettings.calendarPermissionLevel,
    sendMeetingRecap:
      emailRecapSettings?.sendTo === "me" ||
      emailRecapSettings?.sendTo === "participants_with_access",
    shareTeamDataView: DEFAULT_SHARE_TEAM_DATA.view,
    shareTeamDataEdit: DEFAULT_SHARE_TEAM_DATA.edit,
    storeRecordingOptInIsUserEditable: DEFAULT_CAN_EDIT,
    transcriptionOptInIsUserEditable: DEFAULT_CAN_EDIT,
  };
};

const useReviewSettings = () => {
  const { userSettings } = useUserSettings();

  const [workspaceSettings, setWorkspaceSettings] =
    useState<CreateWorkspaceSettings>(buildDefaultSettings(userSettings));

  const updateWorkspaceSettings = <T extends UpdateCreateWorkspaceSetting>(
    setting: T,
    value: UpdateCreateWorkspaceSettings[T]
  ) => {
    const oldSettings = workspaceSettings;
    let newSettings = oldSettings;
    if (setting === "memberSettings") {
      newSettings = {
        ...oldSettings,
        transcriptionOptInIsUserEditable:
          value as UpdateCreateWorkspaceSettings["memberSettings"],
        storeRecordingOptInIsUserEditable:
          value as UpdateCreateWorkspaceSettings["memberSettings"],
      };
    } else if (setting === "joinPreferences") {
      const { type, role } =
        value as UpdateCreateWorkspaceSettings["joinPreferences"];

      newSettings = {
        ...oldSettings,
        calendarJoinInternalOnly: type,
        calendarPermissionLevel: role,
      };
    } else if (setting === "shareTeamDataView") {
      newSettings = {
        ...oldSettings,
        shareTeamDataView: value as EnterpriseRole,
      };
    } else {
      newSettings = { ...oldSettings, [setting]: value };
    }
    setWorkspaceSettings(newSettings);
  };

  return {
    workspaceSettings,
    updateWorkspaceSettings,
  };
};

export default useReviewSettings;
